import type { ErrorCode, ErrorResponse } from '@streamr/shared';

/**
 * Every API failure arrives as `{error:{code,message,details?}}` (foundation
 * contract 3/5). One class parses it once so no component has to guess whether
 * it is holding a string, an Error or a response body.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode | 'NETWORK' | 'UNKNOWN';
  readonly details: unknown;

  constructor(status: number, code: ErrorCode | 'NETWORK' | 'UNKNOWN', message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  get isAuthFailure(): boolean {
    return this.code === 'UNAUTHORIZED';
  }

  get isNotFound(): boolean {
    return this.code === 'NOT_FOUND';
  }
}

const isErrorResponse = (value: unknown): value is ErrorResponse => {
  if (typeof value !== 'object' || value === null) return false;
  const error = (value as { error?: unknown }).error;
  if (typeof error !== 'object' || error === null) return false;
  return typeof (error as { code?: unknown }).code === 'string';
};

/**
 * Turns any failed response into an ApiError. A route that returns something
 * other than the agreed envelope still produces a usable error rather than
 * `[object Object]` in the UI -- but it lands as UNKNOWN, which is the signal
 * that a route is off-contract.
 */
export const toApiError = async (response: Response): Promise<ApiError> => {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = undefined;
  }

  if (isErrorResponse(body)) {
    const { code, message, details } = body.error;
    return new ApiError(response.status, code, message, details);
  }

  return new ApiError(
    response.status,
    'UNKNOWN',
    `${response.status} ${response.statusText || 'request failed'}`.trim(),
    body,
  );
};

/** Human-facing copy. Keyed on the closed code set so it stays exhaustive. */
const MESSAGES: Record<ErrorCode | 'NETWORK' | 'UNKNOWN', string> = {
  VALIDATION_ERROR: 'Some of that is not quite right.',
  UNAUTHORIZED: 'You need to sign in to do that.',
  FORBIDDEN: 'You do not have access to this.',
  NOT_FOUND: 'We could not find that.',
  CONFLICT: 'That conflicts with something that already exists.',
  PAYLOAD_TOO_LARGE: 'That file is too large.',
  UNSUPPORTED_MEDIA_TYPE: 'That file type is not supported.',
  RATE_LIMITED: 'Too many requests. Give it a moment.',
  INTERNAL: 'Something broke on our end.',
  NETWORK: 'Could not reach Streamr. Check your connection.',
  UNKNOWN: 'Something unexpected happened.',
};

/**
 * Prefers the server's message when there is one: the API writes messages for
 * humans ("handle already taken"), and replacing that with a generic string
 * throws away the only useful part of a 409.
 */
export const errorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    return error.message.trim() !== '' ? error.message : MESSAGES[error.code];
  }
  if (error instanceof Error && error.message.trim() !== '') return error.message;
  return MESSAGES.UNKNOWN;
};
